import {
  Breadcrumbs,
  Button,
  Division,
  Flex,
  Heading,
  Modal,
  Paragraph,
  Textarea,
  useToast,
  Table,
  TableColumnType,
  Avatar,
  Span,
  Icon,
  Icons,
  Spin,
} from "@ir/ui";
import { useApproval } from "../hooks/useApproval";
import { Activity, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CategoryKey, CategoryTypeKey, Deduction } from "@/core/entities/deduction";
import { WorkSpaceSettings } from "@/core/entities/workspace";
import { useWorkspace } from "@/hooks";
import { ConversationRepository } from "@/infrastructure/repositories/conversation";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { GetConversationByReferenceIdUseCase, GetMessagesUseCase } from "@/core/usecases/conversation";
import { Conversation, ConversationMessage } from "@/core/entities/conversation";
import { ApprovalsLoadingSpinner } from "./ApprovalsLoadingSpinner";
import { ApprovalCommentsEmptyState } from "./ApprovalsEmptyState";
import { useDeductionDrawer, useRefreshDeductionWorkspace } from "@/presentation/deduction/hooks";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { WorkspacePermissions } from "@/core/permissions";

const conversationRepository = new ConversationRepository(GraphQlAdapter.getInstance().getClient());
const getConversationByReferenceId = new GetConversationByReferenceIdUseCase(conversationRepository);
const getConversationMessages = new GetMessagesUseCase(conversationRepository);

const getApprovalCommentsTableColumns = ({
  workspaceSettings,
}: {
  workspaceSettings: WorkSpaceSettings | null;
}): TableColumnType<ConversationMessage>[] => {
  return [
    {
      key: "sender",
      title: "Sender",
      dataIndex: "sender",
      verticalAlign: "top",
      width: 200,
      cellRender: (_value: unknown, data: ConversationMessage) => {
        return (
          <Flex align="start" justifyContent="start" gap={0.5} wrap="no-wrap">
            <Avatar
              size="small"
              name={data.sender?.fullName ?? ""}
              isDeleteUser={data.sender?.isDeleted ?? false}
              title={data.sender?.isDeleted ? "Removed User" : (data.sender?.fullName ?? "Unassigned")}
              src={data.sender?.avatar ?? ""}
            />
            <Flex direction="column" align="start" justifyContent="start" gap={0.25}>
              <Paragraph className="text-grey-800 m-0 text-sm font-regular">{data.sender?.fullName ?? ""}</Paragraph>
              <Span className="text-grey-500 text-xs font-regular">
                {workspaceSettings?.displayDateFormatWithTime
                  ? data.createdAtFormatted(workspaceSettings.displayDateFormatWithTime)
                  : data.createdAt}
              </Span>
            </Flex>
          </Flex>
        );
      },
    },
    {
      key: "comment",
      title: "Comment",
      dataIndex: "contentHtml",
      verticalAlign: "top",
      cellRender: (_value: unknown, data: ConversationMessage) => {
        return <Division style={{ whiteSpace: "normal" }}>{data.contentHtml}</Division>;
      },
    },
  ];
};

type ApprovalActionType = "APPROVE" | "REJECT" | null;

function getApprovalModalHeaderText(selectedCount: number, action: ApprovalActionType): string {
  if (selectedCount > 1 && action) {
    if (action === "REJECT") return "Return Selected Requests";
    return "Approve Selected Requests";
  }
  if (selectedCount === 1 && action) {
    if (action === "REJECT") return "Return Request";
    return "Approve Request";
  }
  return "Review Request";
}

function getCommentLabelText(action: ApprovalActionType): string {
  if (action === null) return "Add Comment";
  if (action === "REJECT") return "Add Comment (required)";
  return "Add Comment (optional)";
}

interface ApprovalModalProps {
  selectedApprovals: Deduction[];
}

const ApprovalModal = ({ selectedApprovals }: ApprovalModalProps) => {
  const { settings: workspaceSettings } = useWorkspace();
  const {
    isApprovalModalOpen,
    setIsApprovalModalOpen,
    setSelectedApproval,
    setSelectedApprovals,
    approvalAction,
    setApprovalAction,
    sendForApprovalBulk,
  } = useApproval();
  const { clearCurrentExpandedDeductionId, clearDrawerInfo, setDrawerOpen, isDrawerOpen } = useDeductionDrawer();
  const refreshDeductionWorkspace = useRefreshDeductionWorkspace();
  const isDrawerOpenRef = useRef(isDrawerOpen);
  isDrawerOpenRef.current = isDrawerOpen;

  const toast = useToast();
  const [comment, setComment] = useState("");
  const [sendingAction, setSendingAction] = useState<"APPROVE" | "REJECT" | null>(null);
  const [isFetchingApprovalComments, setIsFetchingApprovalComments] = useState<boolean>(false);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isError, setIsError] = useState(false);
  // Snapshots at modal open — keep header/footer/body frozen through submit + exit animation.
  // Clearing these on close (even deferred) still flashes a "Review Request" shell mid-exit.
  const [modalApprovals, setModalApprovals] = useState<Deduction[]>([]);
  const [modalAction, setModalAction] = useState<ApprovalActionType>(null);
  const selectedApprovalsRef = useRef(selectedApprovals);
  selectedApprovalsRef.current = selectedApprovals;
  const approvalActionRef = useRef(approvalAction);
  approvalActionRef.current = approvalAction;

  const isApprovalSending = sendingAction !== null;
  const isApproving = sendingAction === "APPROVE";
  const isReturning = sendingAction === "REJECT";

  // Reset local UI state on open (before paint). Do not reset on close — exit animation
  // still paints children, and resetting mid-exit shows the empty Review Request state.
  useLayoutEffect(() => {
    if (!isApprovalModalOpen) {
      return;
    }
    setModalApprovals(selectedApprovalsRef.current);
    setModalAction(approvalActionRef.current);
    setSendingAction(null);
    setComment("");
    setConversation(null);
    setMessages([]);
    setIsFetchingApprovalComments(false);
    setIsError(false);
  }, [isApprovalModalOpen]);

  // Prefer snapshot once set; fall back to props for the first render before the open effect runs.
  const displayApprovals = modalApprovals.length > 0 ? modalApprovals : selectedApprovals;
  const displayAction = modalApprovals.length > 0 ? modalAction : approvalAction;

  const onCloseModal = useCallback(() => {
    // Only dismiss + clear store. Leave local snapshots (and sending overlay) intact so the
    // react-aria exit animation does not briefly render Approve+Return / "Review Request".
    setIsApprovalModalOpen(false);
    setSelectedApprovals([]);
    setSelectedApproval(null);
    setApprovalAction(null);
  }, [setIsApprovalModalOpen, setSelectedApprovals, setSelectedApproval, setApprovalAction]);

  const handleSendApproval = useCallback(
    async (action: "APPROVE" | "REJECT") => {
      if (action === "REJECT" && comment.length === 0) {
        setIsError(true);
        toast.error({
          content: "Add comment to return",
        });
        return;
      }

      const submittedApprovals = displayApprovals;
      const submittedCount = submittedApprovals.length;

      setSendingAction(action);
      const bulkSendForApprovalResponse = await sendForApprovalBulk(submittedApprovals, comment, action);
      if (bulkSendForApprovalResponse) {
        toast.success({
          content: `${submittedCount} Deduction${submittedCount > 1 ? "s" : ""} ${action === "APPROVE" ? "approved" : "Returned"}`,
        });
        // Tear down the drawer under this modal's overlay first. Closing both in the same
        // paint lets the drawer slide-out show through the modal zoom-out (~200ms each).
        const shouldWaitForDrawerExit = isDrawerOpenRef.current;
        clearDrawerInfo();
        clearCurrentExpandedDeductionId();
        setDrawerOpen(false);
        // the modal exit animation is 220 ms long, so we need to wait for it to complete before closing the modal
        if (shouldWaitForDrawerExit) {
          await new Promise<void>(resolve => {
            globalThis.setTimeout(resolve, 220);
          });
        }
        onCloseModal();
        await refreshDeductionWorkspace(true);
      } else {
        toast.error({
          content: `${submittedCount} Deduction${submittedCount > 1 ? "s" : ""} ${action === "APPROVE" ? "approval" : "return"} failed`,
        });
        setSendingAction(null);
        clearDrawerInfo();
        clearCurrentExpandedDeductionId();
        setDrawerOpen(false);
      }
    },
    [
      displayApprovals,
      comment,
      sendForApprovalBulk,
      toast,
      refreshDeductionWorkspace,
      onCloseModal,
      clearDrawerInfo,
      clearCurrentExpandedDeductionId,
      setDrawerOpen,
    ]
  );

  const fetchConversation = useCallback(
    async (deduction: Deduction) => {
      setIsFetchingApprovalComments(true);
      const conversation = await getConversationByReferenceId.execute({
        referenceId: deduction.id,
        categoryKey: CategoryKey.DEDUCTION,
        categoryTypeKey: CategoryTypeKey.APPROVAL,
      });
      if (conversation) {
        setConversation(conversation);
      } else {
        toast.error({
          content: "Failed to fetch conversation",
        });
      }
    },
    [toast]
  );

  const fetchMessages = useCallback(async () => {
    setIsFetchingApprovalComments(true);
    const messages = await getConversationMessages.execute(
      {
        conversationId: conversation?.id ?? null,
        page: 1,
        limit: 100,
      },
      true
    );
    setIsFetchingApprovalComments(false);
    if (messages) {
      setMessages(messages.messages);
    } else {
      toast.error({
        content: "Failed to fetch comments",
      });
    }
  }, [conversation, toast, setIsFetchingApprovalComments, setMessages]);

  useEffect(() => {
    if (isApprovalModalOpen && displayApprovals.length === 1 && conversation === null) {
      const getConversation = async () => {
        await fetchConversation(displayApprovals[0]);
      };
      getConversation();
    }
  }, [isApprovalModalOpen, displayApprovals, conversation, fetchConversation]);

  useEffect(() => {
    if (isApprovalModalOpen && conversation && messages.length === 0) {
      const getMessages = async () => {
        await fetchMessages();
      };
      getMessages();
    }
  }, [isApprovalModalOpen, conversation, messages.length, fetchMessages]);

  const headerText = getApprovalModalHeaderText(displayApprovals.length, displayAction);

  const headerContent = (
    <>
      <Heading level={6} className="dispute-modal-heading text-grey-700 text-md mb-1">
        {headerText}
      </Heading>
      {displayApprovals.length === 1 && (
        <Division>
          <Breadcrumbs
            items={[
              { label: "Check #" + displayApprovals[0]?.checkNumber },
              { label: displayApprovals[0]?.claimNumber },
            ]}
          />
        </Division>
      )}
    </>
  );

  const footerContent = (
    <Flex direction="row" justifyContent="space-between" gap={1} className="flex-1">
      <Button variant="clear" color="grey" onPress={onCloseModal} isDisabled={isApprovalSending}>
        Cancel
      </Button>
      <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION]}>
        <Flex direction="row">
          {(displayAction === null || displayAction === "REJECT") && (
            <Button
              variant="solid"
              color="error"
              onPress={() => handleSendApproval("REJECT")}
              isDisabled={isReturning || isApprovalSending}
            >
              <Icon
                name={isReturning ? Icons.Loading : Icons.ArrowLeft}
                slot="start"
                className={isReturning ? "icon-fast-loading" : ""}
              />
              Return
            </Button>
          )}
          {(displayAction === null || displayAction === "APPROVE") && (
            <Button
              variant="solid"
              color="success"
              onPress={() => handleSendApproval("APPROVE")}
              isDisabled={isApproving || isApprovalSending}
            >
              <Icon
                name={isApproving ? Icons.Loading : Icons.Check}
                slot="start"
                className={isApproving ? "icon-fast-loading" : ""}
              />
              Approve
            </Button>
          )}
        </Flex>
      </HasAnyPermission>
    </Flex>
  );

  const commentLabel = getCommentLabelText(displayAction);

  const approvalCommentsTableColumns = useMemo(() => {
    return getApprovalCommentsTableColumns({ workspaceSettings });
  }, [workspaceSettings]);

  return (
    <Modal
      isOpen={isApprovalModalOpen}
      onClose={onCloseModal}
      zIndex={12}
      key={"approval-modal"}
      size="medium"
      headerContent={headerContent}
      footerContent={footerContent}
      wrapClassName="approval-modal"
      classNames={{
        header: "p-4 border-bottom-1 border-color-grey-50",
        body: "p-4 border-bottom-1 border-color-grey-50",
        footer: "px-4 py-4",
      }}
      isKeyboardDismissDisabled={true}
      isDismissible={false}
      closable={false}
    >
      <Flex direction="column" align="stretch" style={{ position: "relative" }}>
        <Activity name="approval-modal-comments" mode={displayApprovals.length === 1 ? "visible" : "hidden"}>
          <Flex direction="column" align="stretch">
            <Paragraph className="mb-2 text-grey-800 text-sm font-bold lh-1">Previous Comments</Paragraph>
            <Division
              className="approval-modal-comment-container mb-3 border-1 border-color-grey-100 rounded-medium"
              style={{ minHeight: 250 }}
            >
              <Flex align="center" justifyContent="center" style={{ height: isFetchingApprovalComments ? 250 : 0 }}>
                <ApprovalsLoadingSpinner isVisible={isFetchingApprovalComments} />
              </Flex>

              {!isFetchingApprovalComments && messages.length === 0 && <ApprovalCommentsEmptyState />}

              {!isFetchingApprovalComments && messages.length > 0 && (
                <Table
                  columns={approvalCommentsTableColumns}
                  dataSource={messages}
                  sticky={true}
                  scroll={{ x: "100%", y: 250 }}
                  border="row"
                  aria-label="Approval Comments Table"
                  aria-labelledby="approval-comments-table"
                  className="m-0"
                />
              )}
            </Division>
          </Flex>
        </Activity>
        <Flex direction="column" align="stretch">
          <Paragraph className="text-grey-800 text-sm font-bold lh-1 mb-2">{commentLabel}</Paragraph>
          <Textarea
            minRows={2}
            maxLength={100}
            showCharCount={true}
            placeholder="Enter your comments here..."
            value={comment}
            isWarning={displayAction === "REJECT" && comment.length === 0}
            isRequired={displayAction === "REJECT"}
            isError={isError}
            onChange={e => {
              setComment(e.target.value);
              if (isError && e.target.value.length > 0) {
                setIsError(false);
              }
            }}
          />
        </Flex>

        <Activity mode={isApprovalSending ? "visible" : "hidden"}>
          <Flex
            align="center"
            justifyContent="center"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
            }}
          >
            <Spin spinning />
          </Flex>
        </Activity>
      </Flex>
    </Modal>
  );
};

export { ApprovalModal };
